import { useState, useEffect, useMemo } from 'react'
import { useChannelBullets } from '@/hooks/useChannelBullets'
import { BULLET_KEYS, type BulletKey, bulletKeyToApiKey } from '@/components/products/v2/tabs/content/channels/channel-constants'

function buildBulletsState(): Record<BulletKey, boolean> {
  return Object.fromEntries(BULLET_KEYS.map((k) => [k, false])) as Record<BulletKey, boolean>
}

export interface UseBulletsStateArgs {
  productId?: string
  channelId: string
  includeBullets: boolean
}

export interface UseBulletsStateReturn {
  bulletFields: Record<BulletKey, string>
  bulletsDirty: Record<BulletKey, boolean>
  bulletsAvailable: boolean
  anyBulletDirty: boolean
  updateBullet: (key: BulletKey, value: string) => void
  resetBullets: () => void
}

export function useBulletsState({ productId, channelId, includeBullets }: UseBulletsStateArgs): UseBulletsStateReturn {
  const bulletsQuery = useChannelBullets(
    includeBullets ? productId : undefined,
    includeBullets ? channelId : undefined,
  )

  const initialBullets: Record<BulletKey, string> = useMemo(() => {
    const data = bulletsQuery.data?.bullets
    return Object.fromEntries(
      BULLET_KEYS.map((k) => [k, data?.[bulletKeyToApiKey(k)] ?? '']),
    ) as Record<BulletKey, string>
  }, [bulletsQuery.data])

  const [bulletFields, setBulletFields] = useState<Record<BulletKey, string>>(initialBullets)
  const [bulletsDirty, setBulletsDirty] = useState<Record<BulletKey, boolean>>(() => buildBulletsState())

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional resync of bullet fields when channel/initial data changes
    setBulletFields(initialBullets)
    setBulletsDirty(buildBulletsState())
  }, [initialBullets, channelId])

  const bulletsAvailable = useMemo(() => {
    if (!includeBullets) return false
    if (channelId === '1-1-0') return false // master has no bullets mapping
    return !!bulletsQuery.data
  }, [includeBullets, channelId, bulletsQuery.data])

  const anyBulletDirty = useMemo(() => BULLET_KEYS.some((k) => bulletsDirty[k]), [bulletsDirty])

  const updateBullet = (key: BulletKey, value: string) => {
    setBulletFields((prev) => ({ ...prev, [key]: value }))
    setBulletsDirty((prev) => ({ ...prev, [key]: true }))
  }

  const resetBullets = () => {
    setBulletFields(initialBullets)
    setBulletsDirty(buildBulletsState())
  }

  return {
    bulletFields,
    bulletsDirty,
    bulletsAvailable,
    anyBulletDirty,
    updateBullet,
    resetBullets,
  }
}
